/**
 * Status → timeline mapping for the public delivery tracking page
 * (`/track/:token`, see PublicDeliveryTrackingScreen).
 *
 * The backend (`GET /v1/delivery-mgmt/public/track/{token}`) returns a raw
 * `status` string (DMS enum value, upper case: IN_TRANSIT, AT_CUSTOMER) plus a pre-localized `statusLabel: {en, ar}` for display.
 * This module only decides which of the six timeline steps are "reached"
 * and whether polling should keep going — it never invents copy, so a new
 * backend status string degrades to "unknown step reached" rather than
 * throwing.
 */

export const TIMELINE_STEPS = [
  "assigned",
  "at_store",
  "picked_up",
  "on_the_way",
  "at_customer",
  "delivered",
] as const;

export type TimelineStep = (typeof TIMELINE_STEPS)[number];

/**
 * Raw backend statuses (lower-cased) mapped to the timeline step they satisfy.
 * Multiple raw statuses can map to the same step.
 */
const STATUS_TO_STEP: Record<string, TimelineStep> = {
  assigned: "assigned",
  driver_assigned: "assigned",
  accepted: "assigned",
  pickup_in_progress: "assigned",
  at_store: "at_store",
  picked_up: "picked_up",
  pickup_complete: "picked_up",
  collected: "picked_up",
  on_the_way: "on_the_way",
  en_route: "on_the_way",
  in_transit: "on_the_way",
  at_customer: "at_customer",
  arrived: "at_customer",
  delivered: "delivered",
  completed: "delivered",
};

/** Statuses that mean the trip is over: polling must stop on these. */
const TERMINAL_STATUSES = new Set([
  "delivered", "completed", "cancelled", "canceled", "failed", "failed_delivery", "returned_to_store",
]);

/** DMS returns enum values (IN_TRANSIT); the mirror uses in_transit. Compare lower-case. */
function norm(status: string | null | undefined): string {
  return (status ?? "").trim().toLowerCase();
}

/**
 * Index (0-based) of the last timeline step reached, or -1 if the status is
 * unrecognized (nothing highlighted rather than a guess).
 */
export function stepIndexForStatus(status: string | null | undefined): number {
  const step = STATUS_TO_STEP[norm(status)];
  return step ? TIMELINE_STEPS.indexOf(step) : -1;
}

/** Whether a given timeline step has been reached by the current status. */
export function isStepReached(status: string | null | undefined, step: TimelineStep): boolean {
  const idx = stepIndexForStatus(status);
  if (idx === -1) return false;
  return TIMELINE_STEPS.indexOf(step) <= idx;
}

/**
 * A trip is "done" (delivered or cancelled/failed): the page should stop
 * polling and show a final state rather than keep refreshing forever.
 */
export function isTerminalStatus(status: string | null | undefined): boolean {
  return TERMINAL_STATUSES.has(norm(status));
}

/** True only for a successful delivery, as opposed to cancelled/failed. */
export function isDeliveredStatus(status: string | null | undefined): boolean {
  const s = norm(status);
  return s === "delivered" || s === "completed";
}

/** True for a cancelled/failed trip: shown as a distinct end state, not a timeline step. */
export function isCancelledStatus(status: string | null | undefined): boolean {
  const s = norm(status);
  return s === "cancelled" || s === "canceled" || s === "failed" || s === "failed_delivery" || s === "returned_to_store";
}
